import React from 'react';
import { Box, Collapse, Skeleton, Typography } from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import { Surface } from './Surface';
import { Modal } from '../overlays/Modal';
import { InfoHint, type InfoHintProps } from '../data-display/InfoHint';
import { ToolbarButton } from '../inputs/ToolbarButton';
import { ActionMenu, type ActionMenuItem } from '../inputs/ActionMenu';
import { isSurfaceClick } from '../utils/events';
import { useElementWidth } from '../hooks/useElementWidth';

export interface PanelProps {
  /**
   * A control at the start of the header, before any title — e.g. a switch
   * between views, when the panel is named by something around it.
   */
  headerStart?: React.ReactNode;
  title?: React.ReactNode;
  /** One line under the title: what the panel shows. */
  subtitle?: React.ReactNode;
  /** How to read the panel, behind an info icon beside the title. */
  info?: React.ReactNode;
  /** `warning` turns the info icon yellow: the panel is worked out in a way worth reading first. */
  infoTone?: InfoHintProps['tone'];
  /**
   * One header row instead of two: the title on the left and every control
   * on its right — for a small card whose controls are a few icons (a
   * dashboard widget, a chat result), where a toolbar row of its own would be
   * an empty band and take half the card. Board panels keep the two rows.
   */
  compact?: boolean;
  /** A search box over the panel's rows, on the right of the title row. */
  search?: React.ReactNode;
  /**
   * Controls that change what the panel shows — a switch between views, a
   * picker — on the left of the toolbar row.
   */
  tools?: React.ReactNode;
  /**
   * What to do with the panel — Download, Save, a link elsewhere — on the
   * right of the toolbar row, before Columns, Expand and ⋯.
   */
  actions?: React.ReactNode;
  /** Notes and caveats, in a strip along the bottom. */
  footer?: React.ReactNode;
  /** Shimmer the body only; the header stays live so its controls still work. */
  loading?: boolean;
  /** Height the loading placeholder holds, px. Match the content so the page does not jump. */
  loadingHeight?: number;
  /** A button in the header folds the panel down to its header. */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Remember whether it was left folded, per browser, under this key. */
  storageKey?: string;
  /** No padding around the body — for tables and anything that runs edge to edge. */
  flush?: boolean;
  /**
   * Adds an Expand button that opens the panel nearly full screen, under the
   * same title and subtitle. Pass what to show there — usually the same
   * chart, drawn to fill the height. `title` names it when the panel itself
   * has none.
   *
   * `actions` gives the window its own controls. Worth setting whenever a
   * header control stops making sense at full size — a shortcut that opens
   * something beside the panel has nothing to sit beside once the panel fills
   * the screen. Left out, the panel's own actions carry over.
   */
  expanded?: { content: React.ReactNode; title?: React.ReactNode; actions?: React.ReactNode };
  /**
   * The panel's less-common actions, behind one ⋯ button at the end of the
   * header. Expand joins them there rather than sitting beside them, so a
   * busy header is one button instead of four.
   */
  menu?: ActionMenuItem[];
  /**
   * Never taller than the space it's given: the header and footer stay put
   * and the body scrolls — for a panel filling a pane. Shorter content keeps
   * its own height.
   */
  bounded?: boolean;
  /**
   * Take exactly the height its container gives it, however little content
   * there is: the header and footer stay put and the body scrolls. For a
   * panel in a sized cell — a dashboard widget — where the reader decides how
   * tall it is by resizing, not the content.
   */
  fill?: boolean;
  children: React.ReactNode;
}

const STORAGE_PREFIX = 'klaris_panel_collapsed_';

function readStored(key?: string): boolean | null {
  if (!key) return null;
  try {
    const value = window.localStorage.getItem(STORAGE_PREFIX + key);
    return value === null ? null : value === '1';
  } catch {
    return null;
  }
}

/**
 * The box a chart or table sits in: header, body, and an optional footer.
 *
 * Every panel has the same shape (Anuj, 21 Oct 2026). The first row is the
 * title and what the panel shows, with only a search box and the fold button
 * on its right. Under a dividing line, the toolbar row holds every other
 * control: on the left, `tools` that change what the panel shows (view
 * switches, pickers), read first like the rest of the page; on the right,
 * under the search box, what to do with it, always in the same order —
 * `actions` (Download, Save…), then Columns, Expand and ⋯. A panel with none
 * has no toolbar row. So a reader finds a control in the same place
 * on every panel, however long the subtitle or narrow the screen; before,
 * the controls sat beside the title or dropped under it depending on the fit.
 * A `compact` card (a dashboard widget, a chat result) keeps a single row:
 * the title, then its few controls on the right.
 * Caveats go in the footer strip, in view rather than hidden.
 *
 * `collapsible` adds an Expand / Collapse button at the header's end and
 * makes the whole header clickable, for long panels a reader may want out of
 * the way; `storageKey` remembers the choice. Folded, only the title shows —
 * its controls would have nothing to act on.
 *
 * Built on `Surface`. For a single headline number, use `MetricCard`.
 */
/**
 * Where a panel keeps room in its header for controls that belong to what is
 * inside it — a table's column picker, say.
 *
 * A control like that is the table's, not the page's, so the table renders it;
 * but it belongs beside Download and Expand, not floating over the rows. The
 * panel offers the space and whatever is inside fills it, so no page has to
 * pass a control through for a table it already contains.
 *
 * `roomy` is false in a narrow panel, where the same controls drop their
 * labels and keep their icons.
 */
export const PanelActions = React.createContext<{
  slot: HTMLElement | null;
  roomy: boolean;
  /**
   * The panel keeps its actions behind a ⋯ menu, so a control from inside
   * belongs there too rather than beside it — Expand already works this way.
   * `addMenuItem` puts one in and returns the call that takes it out again.
   */
  hasMenu: boolean;
  addMenuItem?: (item: ActionMenuItem) => () => void;
  /**
   * Say a control is in `slot`, so the toolbar row shows even on a panel
   * with no controls of its own. Returns the call that says it has gone.
   */
  occupy?: () => () => void;
}>({
  slot: null,
  roomy: true,
  hasMenu: false,
});

/** Under this many px a panel header has no room for words beside its icons. */
const ROOMY_FROM = 720;

export function Panel({
  headerStart,
  title,
  subtitle,
  info,
  infoTone,
  compact = false,
  search,
  tools,
  actions,
  footer,
  loading = false,
  loadingHeight = 200,
  collapsible = false,
  defaultCollapsed = false,
  storageKey,
  flush = false,
  expanded,
  menu,
  bounded = false,
  fill = false,
  children,
}: PanelProps) {
  const bodyId = React.useId();
  const [collapsed, setCollapsed] = React.useState(() => (collapsible ? readStored(storageKey) ?? defaultCollapsed : false));
  const [expandOpen, setExpandOpen] = React.useState(false);
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);
  const [inner, setInner] = React.useState<ActionMenuItem[]>([]);
  const addMenuItem = React.useCallback((item: ActionMenuItem) => {
    setInner((prev) => [...prev.filter((i) => i.key !== item.key), item]);
    return () => setInner((prev) => prev.filter((i) => i.key !== item.key));
  }, []);
  // How many controls from inside sit in `slot` (a table's column picker).
  const [occupants, setOccupants] = React.useState(0);
  const occupy = React.useCallback(() => {
    setOccupants((n) => n + 1);
    return () => setOccupants((n) => n - 1);
  }, []);
  const [expandedSlot, setExpandedSlot] = React.useState<HTMLElement | null>(null);
  const [widthRef, width] = useElementWidth<HTMLDivElement>();
  const roomy = width === 0 || width >= ROOMY_FROM;
  // Held steady between renders: a fresh object here would re-run every effect
  // inside the panel that reads this context.
  const bodyActions = React.useMemo(
    () => ({ slot, roomy, hasMenu: !!menu?.length, addMenuItem, occupy }),
    [slot, roomy, menu?.length, addMenuItem, occupy],
  );
  const expandedActions = React.useMemo(
    () => ({ slot: expandedSlot, roomy: true, hasMenu: false }),
    [expandedSlot],
  );
  // A subtitle alone still earns a header — a result's facts with no title.
  const hasHeader = Boolean(headerStart || title || subtitle || search || tools || actions || collapsible || expanded || menu?.length);
  // The toolbar row: only when there is a control to put in it.
  const hasToolbar = Boolean(tools || actions || expanded || menu?.length || occupants > 0);
  const expandName = expanded?.title ?? title;
  const titleText = typeof title === 'string' ? title : 'this panel';

  // What to do with the panel, in the same order everywhere: its own
  // actions, then what its contents add (Columns), Expand and ⋯. Drawn once,
  // in the toolbar row or, on a compact card, on the title row.
  const endControls = (
    <>
      {actions}
      {/* Filled by whatever is inside the panel — see `PanelActions`.
          `display: contents` so whatever lands here is spaced by this
          row like every other control, and an empty slot adds no gap
          of its own. */}
      <Box ref={setSlot} sx={{ display: 'contents' }} />
      {expanded && !menu?.length && (
        <ToolbarButton
          icon={<OpenInFullRounded fontSize="small" />}
          onClick={() => setExpandOpen(true)}
          tooltip="Expand"
          aria-label={`Expand ${typeof expandName === 'string' ? expandName : titleText}`}
        >
          {/* Named where there is room; in a narrow panel the icon and
              its tooltip carry it. */}
          {roomy ? 'Expand' : undefined}
        </ToolbarButton>
      )}
      {!!menu?.length && (
        <ActionMenu
          aria-label={`More actions for ${titleText}`}
          icon={<MoreHorizRounded fontSize="small" />}
          items={[
            ...(expanded
              ? [
                  {
                    key: 'expand',
                    label: 'Expand',
                    icon: <OpenInFullRounded fontSize="small" />,
                    onSelect: () => setExpandOpen(true),
                  },
                ]
              : []),
            // Controls the panel's own contents put there.
            ...inner,
            ...menu,
          ]}
        />
      )}
    </>
  );

  const toggle = () =>
    setCollapsed((prev) => {
      const next = !prev;
      if (storageKey) {
        try {
          window.localStorage.setItem(STORAGE_PREFIX + storageKey, next ? '1' : '0');
        } catch {
          // Not remembered; the panel still folds.
        }
      }
      return next;
    });

  const body = (
    <>
      <Box
        sx={{
          p: flush ? 0 : 2,
          flex: bounded ? '1 1 auto' : 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          ...((bounded || fill) && { minHeight: 0, overflow: 'auto' }),
        }}
      >
        <PanelActions.Provider value={bodyActions}>
          {loading ? <Skeleton variant="rounded" height={loadingHeight} /> : children}
        </PanelActions.Provider>
      </Box>
      {footer && (
        <Box
          sx={(t) => ({
            px: 2,
            py: 1,
            flexShrink: 0,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface.sunken,
          })}
        >
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {footer}
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Surface
      ref={widthRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(bounded && { flex: '0 1 auto', minHeight: 0 }),
        ...(fill && { height: '100%' }),
      }}
    >
      {hasHeader && (
        <Box
          sx={(t) => ({
            flexShrink: 0,
            borderBottom: collapsed ? 0 : `1px solid ${t.tokens.border.subtle}`,
            // Every panel's header carries the brand tint, so tables and charts
            // read as one family wherever they sit.
            bgcolor: t.tokens.brand.primarySofter,
          })}
        >
          {/* Row one: words only. The whole row folds and unfolds a
              collapsible panel — a full-width target rather than a small
              button. The labelled button stays for keyboard users; the row
              adds nothing to the tab order. */}
          <Box
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              if (collapsible && isSurfaceClick(e)) toggle();
            }}
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              columnGap: 2,
              px: 2,
              py: 1.25,
              ...(collapsible && {
                cursor: 'pointer',
                transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                '&:hover': { bgcolor: t.tokens.brand.primarySoft },
              }),
            })}
          >
            {/* A control at the start (a view switch) keeps its full width;
                the title and subtitle take the rest and wrap. */}
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              {headerStart}
              {/* The name wraps rather than being cut off, and its icon follows
                  the last word instead of standing off at the right. */}
              {(title || info) && (
                <Typography variant="subtitle2" component="h3" sx={{ minWidth: 0 }}>
                  {title}
                  {info && (
                    <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 0.5 }}>
                      <InfoHint title={info} label={`About ${titleText}`} tone={infoTone} />
                    </Box>
                  )}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="caption" sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary })}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {compact && !collapsed && tools}
            {/* Search stays with the name: it narrows what the title names. */}
            {search && !collapsed && (
              <Box sx={{ flex: '0 1 280px', minWidth: 140 }}>{search}</Box>
            )}
            {compact && !collapsed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{endControls}</Box>
            )}
            {/* Folding is about the panel itself, so it stays with its name. */}
            {collapsible && (
              <ToolbarButton
                variant="ghost"
                onClick={toggle}
                aria-expanded={!collapsed}
                aria-controls={bodyId}
                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${titleText}`}
                icon={
                  <ExpandMoreRounded
                    sx={(t) => ({
                      transform: collapsed ? 'rotate(-90deg)' : 'none',
                      transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                    })}
                  />
                }
              >
                {collapsed ? 'Expand' : 'Collapse'}
              </ToolbarButton>
            )}
          </Box>

          {/* Row two, under a dividing line: the toolbar, the same on every
              panel. Always drawn, so a table inside can put its own controls
              in `slot`; hidden while there is nothing in it, and while the
              panel is folded. */}
          {!compact && (
          <Box
            role="toolbar"
            aria-label={`${titleText} tools`}
            sx={(t) => ({
              display: hasToolbar && !collapsed ? 'flex' : 'none',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              px: 2,
              py: 1,
              minWidth: 0,
              borderTop: `1px solid ${t.tokens.border.subtle}`,
            })}
          >
            {/* What the panel shows: view switches and pickers. They give way
                first in a narrow panel. */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, flex: '1 1 auto', minWidth: 0 }}>
              {tools}
            </Box>
            {/* What to do with it, on the right under the search box. Never
                wraps: in a narrow panel the buttons keep their icons and drop
                their words. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}>
            {endControls}
            </Box>
          </Box>
          )}
        </Box>
      )}

      {collapsible ? (
        <Collapse in={!collapsed} id={bodyId}>
          {body}
        </Collapse>
      ) : (
        body
      )}

      {expanded && (
        <Modal
          open={expandOpen}
          onClose={() => setExpandOpen(false)}
          title={expandName ?? ''}
          subtitle={subtitle}
          actions={
            <>
              {/* The panel's own search and tools carry over unless the window
                  has its own. */}
              {!expanded.actions && search}
              {!expanded.actions && tools}
              <Box ref={setExpandedSlot} sx={{ display: 'contents' }} />
              {expanded.actions ?? actions}
            </>
          }
          size="full"
        >
          {/* Never squeezed to nothing: on a short window the body scrolls
              instead of the content spilling over the note under it. */}
          <Box sx={{ flex: '1 1 auto', minHeight: 320, display: 'flex', flexDirection: 'column' }}>
            <PanelActions.Provider value={expandedActions}>
              {expanded.content}
            </PanelActions.Provider>
          </Box>
          {footer && (
            <Typography variant="caption" sx={(t) => ({ display: 'block', mt: 1.5, color: t.tokens.text.secondary })}>
              {footer}
            </Typography>
          )}
        </Modal>
      )}
    </Surface>
  );
}
